/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { softwareList } from '@src/configs/software.config'
import { deviceStats } from '@src/configs/devices.generated'

import { useNavigate } from 'react-router-dom'

/**
 * Support 허브 — Monolith(2안), Swiss Modernism 구성.
 *
 * 사이드 네비·버튼 나열 대신 번호 매긴 인덱스 4행(Manuals · Software · Works with · FAQ).
 * 각 행은 4:8 비대칭 — 왼쪽에 번호와 제목, 오른쪽에 한 줄 설명과 바로 가는 링크.
 * URL 체계는 judahsWeb/PENDING_CHANGES.md §12, 사이트맵은 SITEMAP_PLAN.md §9 를 따른다.
 * Privacy / Store Policy 는 이 허브에서 뺐다 (푸터에만 둔다, 2026-09-08).
 * Contact 은 GNB 에 있으므로 여기서도 뺐다 — 한 페이지에 자리 하나 (2026-09-09).
 * Support 는 "제품을 쓰는 데 필요한 자료" 만 담는다. 사람(Contact)·커뮤니티(Forum)는 밖.
 */
/**
 * Lucide `chevron-right` (ISC) 원본 그대로 — 24 viewBox, path "m9 18 6-6-6-6".
 * Lucide 가 공식 지원하는 strokeWidth 만 1 로 낮춰 얇게, size 로 키워 길게 쓴다.
 */
const Chevron = () => (
	<svg
		className="mnIndexArrow"
		xmlns="http://www.w3.org/2000/svg"
		width={56}
		height={56}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m9 18 6-6-6-6" />
	</svg>
)

const MonolithSupportPage = () => {
	// hook
	const navigate = useNavigate()

	const go = (path: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(path)
	}

	return (
		<View css={style}>
			<Seo />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Support</h1>
					<p className="mnPageLede">Manuals, software, compatibility and answers.</p>
				</View>

				<View className="mnIndex">
					{/* 01 Manuals */}
					<a className="mnIndexRow" href="/manuals" onClick={go('/manuals')}>
						<View className="mnIndexHead">
							<span className="mnIndexNo">01</span>
							<h2 className="mnIndexTitle">Manuals</h2>
						</View>
						<View className="mnIndexBody">
							<p className="mnIndexDesc">
								User manuals for every product, as PDF, in up to ten languages.
							</p>
							<View className="mnIndexLinks">
								<span className="mnIndexLink">Kedem 6</span>
								<span className="mnIndexLink">Kedem 4</span>
								<span className="mnIndexLink">Koinos PLS 8</span>
							</View>
						</View>
						<Chevron />
					</a>

					{/* 02 Software */}
					<a className="mnIndexRow" href="/software" onClick={go('/software')}>
						<View className="mnIndexHead">
							<span className="mnIndexNo">02</span>
							<h2 className="mnIndexTitle">Software</h2>
						</View>
						<View className="mnIndexBody">
							<p className="mnIndexDesc">
								Desktop software for Judahs hardware. Downloads and requirements.
							</p>
							<View className="mnIndexLinks">
								{softwareList.map(aSoftware => (
									<span key={aSoftware.slug} className="mnIndexLink">
										{aSoftware.name}
									</span>
								))}
								<span className="mnIndexLink mnIndexLinkMuted">macOS · Windows</span>
							</View>
						</View>
						<Chevron />
					</a>

					{/* 03 Works with */}
					<a className="mnIndexRow" href="/works-with" onClick={go('/works-with')}>
						<View className="mnIndexHead">
							<span className="mnIndexNo">03</span>
							<h2 className="mnIndexTitle">Works with</h2>
						</View>
						<View className="mnIndexBody">
							<p className="mnIndexDesc">
								The devices our hardware controls over MIDI, by manufacturer.
							</p>
							<View className="mnIndexLinks">
								<span className="mnIndexLink">Amp modelers</span>
								<span className="mnIndexLink">Multi-effects</span>
								<span className="mnIndexLink">Pedals</span>
								<span className="mnIndexLink mnIndexLinkMuted">
									{deviceStats.manufacturers} manufacturers · {deviceStats.devices}{' '}
									devices
								</span>
							</View>
						</View>
						<Chevron />
					</a>

					{/* 04 FAQ */}
					<a className="mnIndexRow" href="/faq" onClick={go('/faq')}>
						<View className="mnIndexHead">
							<span className="mnIndexNo">04</span>
							<h2 className="mnIndexTitle">FAQ</h2>
						</View>
						<View className="mnIndexBody">
							<p className="mnIndexDesc">
								Orders, shipping and warranty. How to use a product is in its manual.
							</p>
							<View className="mnIndexLinks">
								<span className="mnIndexLink">Orders</span>
								<span className="mnIndexLink">Shipping</span>
								<span className="mnIndexLink">Warranty</span>
							</View>
						</View>
						<Chevron />
					</a>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithSupportPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnIndex': {
		borderTop: `1px solid ${mono.line}`,
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	'.mnIndexRow': {
		position: 'relative',
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(36px, 3.6vw, 52px) 0',
		borderBottom: `1px solid ${mono.line}`,
		color: mono.ink,
		textDecoration: 'none',
		cursor: 'pointer',
		transition: 'background-color 200ms ease-out',

		'&:hover': {
			backgroundColor: mono.surface,
		},
		'&:hover .mnIndexArrow': {
			transform: 'translateX(6px)',
			color: mono.ink,
		},
		'&:hover .mnIndexTitle': {
			color: mono.ink,
		},
		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: -1,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 20,
		},
	},

	'.mnIndexHead': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 24,

		'.mnIndexNo': {
			fontSize: 12,
			letterSpacing: '.22em',
			color: mono.faint,
			fontWeight: 500,
			fontVariantNumeric: 'tabular-nums',
			minWidth: 28,
		},
		'.mnIndexTitle': {
			fontSize: 'clamp(28px, 2.8vw, 40px)',
			lineHeight: 1.1,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	'.mnIndexBody': {
		display: 'flex',
		flexDirection: 'column',
		gap: 18,
		paddingRight: 120,

		'.mnIndexDesc': {
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 560,
		},

		'.mnIndexLinks': {
			display: 'flex',
			flexWrap: 'wrap',
			gap: '8px 28px',

			'.mnIndexLink': {
				fontSize: 13,
				fontWeight: 500,
				color: mono.ink,
				letterSpacing: '.02em',
			},
			'.mnIndexLinkMuted': {
				fontWeight: 400,
				color: mono.faint,
			},
		},

		'@media (max-width: 900px)': {
			paddingRight: 40,
		},
	},

	'.mnIndexArrow': {
		position: 'absolute',
		right: 24,
		top: '50%',
		transform: 'translateY(-50%)',
		display: 'block',
		color: mono.faint,
		transition: 'transform 200ms ease-out, color 200ms ease-out',
		// translateY 와 hover translateX 를 합친다
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	'.mnIndexRow:hover .mnIndexArrow': {
		transform: 'translateY(-50%) translateX(6px)',
	},
})
